import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { INTEGRATION_ICONS } from '../mock';
import { Button } from './ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { hireWorker } from '../api';

const WorkerCard = ({ worker }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleHire = async () => {
    if (worker.comingSoon) {
      toast({ title: `${worker.name} is coming soon`, description: 'Join the waitlist to be first to hire.' });
      return;
    }
    setLoading(true);
    try {
      await hireWorker({ workerId: worker.id, workerName: worker.name, role: worker.role, isCustom: false });
      toast({ title: `Hired ${worker.name}`, description: 'Opening your workspace…' });
      setTimeout(() => navigate('/workspace'), 600);
    } catch (e) {
      toast({ title: 'Hire failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="group relative flex h-full flex-col rounded-2xl border border-neutral-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_18px_36px_-16px_rgba(0,0,0,0.12)]">
      {worker.comingSoon && (
        <div className="absolute right-4 top-4 rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">
          Coming soon
        </div>
      )}

      <div className="mb-4 flex items-start gap-3">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br ${worker.avatarColor} text-lg font-semibold text-neutral-800 ring-1 ring-inset ring-white/60`}>
          {worker.avatar ? (
            <img src={worker.avatar} alt={worker.name} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            worker.initial
          )}
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-[17px] font-semibold text-neutral-900">{worker.name}</h3>
          <p className="truncate text-sm text-neutral-500">{worker.role}</p>
        </div>
      </div>

      <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-neutral-600">{worker.description}</p>

      <ul className="mb-4 space-y-1.5">
        {worker.capabilities.slice(0, 3).map((cap) => (
          <li key={cap} className="flex items-start gap-2 text-[13px] text-neutral-700">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
            <span className="leading-snug">{cap}</span>
          </li>
        ))}
      </ul>

      {worker.tools?.length > 0 && (
        <div className="mb-4 flex items-center gap-1.5">
          {worker.tools.slice(0, 5).map((tool, idx) => (
            <div key={tool + idx} className="flex h-7 w-7 items-center justify-center rounded-md border border-neutral-200 bg-white p-1">
              <img src={INTEGRATION_ICONS[tool]} alt={tool} className="h-full w-full object-contain" />
            </div>
          ))}
          {worker.extra > 0 && (
            <div className="flex h-7 min-w-[28px] items-center justify-center rounded-md border border-neutral-200 bg-neutral-50 px-1.5 text-[11px] font-medium text-neutral-600">
              +{worker.extra}
            </div>
          )}
        </div>
      )}

      {worker.tags?.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-1.5">
          {worker.tags.map((tag) => (
            <span key={tag} className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-600">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto">
        <Button
          onClick={handleHire}
          disabled={worker.comingSoon || loading}
          className={
            worker.comingSoon
              ? 'w-full cursor-not-allowed bg-neutral-100 text-neutral-500 hover:bg-neutral-100'
              : 'w-full bg-neutral-900 text-white hover:bg-neutral-800'
          }
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (worker.comingSoon ? 'Soon' : 'Hire')}
        </Button>
      </div>
    </div>
  );
};

export default WorkerCard;
